<div class="type-exclusions-panel">
  <mat-accordion multi="true">
    <!-- Main Exclusions Panel -->
    <mat-expansion-panel [expanded]="hasExclusions">
      <mat-expansion-panel-header>
        <mat-panel-title>
          Exclude CPs by Component or Event Type
        </mat-panel-title>
        <mat-panel-description>
          Exclude constant properties from locking based on their component or event type
        </mat-panel-description>
      </mat-expansion-panel-header>

      <div class="exclusions-content">
        <!-- Component Exclusions Section -->
        <ng-container
          [ngTemplateOutlet]="exclusionSectionTemplate"
                    [ngTemplateOutletContext]="{
                        group: componentGroup,
                        title: 'Component Type Exclusions',
                        typeLabel: 'component',
                        selectComponent: 'select-many-component-types'
                    }">
        </ng-container>

        <mat-divider></mat-divider>

        <!-- Event Exclusions Section -->
        <ng-container
          [ngTemplateOutlet]="exclusionSectionTemplate"
                    [ngTemplateOutletContext]="{
                        group: eventGroup,
                        title: 'Event Type Exclusions',
                        typeLabel: 'event',
                        selectComponent: 'select-many-event-types'
                    }">
        </ng-container>
      </div>
    </mat-expansion-panel>
  </mat-accordion>
</div>

<!-- Reusable Exclusion Section Template -->
<ng-template #exclusionSectionTemplate let-group="group" let-title="title" let-typeLabel="typeLabel" let-selectComponent="selectComponent">
  <div class="exclusion-section">
    <div class="section-header">
      <h4>{{ title }}</h4>
      <button
        mat-button
        color="primary"
        (click)="toggleAddPanel(group)"
        [disabled]="disabled || !hasAvailableTypes(typeLabel)"
        [matTooltip]="getAddButtonTooltip(typeLabel)"
        [matTooltipDisabled]="!disabled && hasAvailableTypes(typeLabel)">
        <mat-icon>add</mat-icon>
        Add {{ typeLabel | titlecase }} Exclusion
      </button>
    </div>

    <!-- Add Panel -->
    @if (group.showAddPanel) {
      <div class="add-panel">
        <!-- Dynamic component selector -->
        @if (selectComponent === 'select-many-component-types') {
          <select-many-component-types
            [value]="group.selectedNewTypes"
            [disabled]="disabled"
            [custom_filters]="getTypeFilters(typeLabel)"
            placeholder="Select {{ typeLabel }} types to exclude"
            (selectionChange)="onNewTypesSelectionChange(group, $event)">
          </select-many-component-types>
        }
        @if (selectComponent === 'select-many-event-types') {
          <select-many-event-types
            [value]="group.selectedNewTypes"
            [disabled]="disabled"
            [custom_filters]="getTypeFilters(typeLabel)"
            placeholder="Select {{ typeLabel }} types to exclude"
            (selectionChange)="onNewTypesSelectionChange(group, $event)">
          </select-many-event-types>
        }
        <div class="add-panel-actions">
          <button
            mat-button
            (click)="toggleAddPanel(group)">
            Cancel
          </button>
          <button
            mat-raised-button
            color="primary"
            (click)="onNewTypesSelected(group, group.selectedNewTypes)"
            [disabled]="!group.selectedNewTypes || group.selectedNewTypes.length === 0">
            Add Selected{{ group.selectedNewTypes?.length ? ' (' + group.selectedNewTypes.length + ')' : '' }}
          </button>
        </div>
      </div>
    }

    <!-- Exclusions List -->
    <div class="exclusions-tree">
      @for (entry of getGroupExclusionsArray(group); track trackByTypeId($index, entry)) {
        <ng-container
          [ngTemplateOutlet]="typeNodeTemplate"
          [ngTemplateOutletContext]="{ entry: entry, group: group }">
        </ng-container>
      }

      @if (group.exclusions.size === 0 && !group.showAddPanel) {
        <div class="empty-state">
          No {{ typeLabel }} type exclusions configured
        </div>
      }
    </div>
  </div>
</ng-template>

<!-- Reusable Type Node Template -->
<ng-template #typeNodeTemplate let-entry="entry" let-group="group">
  <div class="type-node">
    <!-- Type Header -->
    <div class="type-header">
      <button
        mat-icon-button
        (click)="toggleExpand(entry[1], group)"
        [disabled]="disabled"
        class="expand-btn">
        <mat-icon>
          {{ entry[1].isExpanded ? 'expand_more' : 'chevron_right' }}
        </mat-icon>
      </button>

      <span class="type-name">{{ entry[1].name }}</span>

      <mat-chip-listbox class="exclusion-status">
        @if (entry[1].selectedCPIds.size === 0) {
          <mat-chip color="warn" selected>
            All CPs excluded
          </mat-chip>
        }
        @if (entry[1].selectedCPIds.size > 0) {
          <mat-chip>
            {{ entry[1].selectedCPIds.size }} CPs excluded
          </mat-chip>
        }
      </mat-chip-listbox>

      <div class="type-actions">
        <button
          mat-icon-button
          (click)="removeType(entry[0], group)"
          [disabled]="disabled"
          matTooltip="Remove exclusion">
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>

    <!-- Constant Properties -->
    @if (entry[1].isExpanded) {
      <div class="cp-content">
        @if (entry[1].cpLoading) {
          <div class="cp-loading">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          </div>
        }
        @if (!entry[1].cpLoading && entry[1].cpLoaded) {
          <div class="cp-list">
            <div class="cp-actions">
              <mat-checkbox
                class="cp-select-all-checkbox"
                [checked]="isAllCPsSelected(entry[1])"
                (change)="selectAllCPs(entry[1])"
                [disabled]="disabled"
                matTooltip="Toggle exclusion for all constant properties">
                Exclude All ({{ entry[1].constantProperties.length }} properties)
              </mat-checkbox>
            </div>
            <div class="cp-grid">
              @for (cp of entry[1].constantProperties; track trackByCPId($index, cp)) {
                <mat-checkbox
                  [checked]="entry[1].selectedCPIds.has(cp.id)"
                  (change)="toggleCP(entry[1], cp.id)"
                  [disabled]="disabled"
                  class="cp-checkbox">
                  {{ cp.attributes.name }}
                </mat-checkbox>
              }
            </div>
            @if (entry[1].constantProperties.length === 0) {
              <div class="no-cps">
                No constant properties found for this type
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</ng-template>
